import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Shield, Lock, Eye, Database, Mail } from 'lucide-react';

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-slate-600 hover:text-[#344B71]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-[#344B71] to-[#344B71] rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-playfair font-bold text-[#344B71] mb-4">
              Privacy Policy
            </h1>
            <p className="text-slate-600">
              Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            <Card className="p-8 bg-white">
              <div className="flex items-center space-x-3 mb-4">
                <Database className="w-6 h-6 text-[#344B71]" />
                <h2 className="text-xl font-semibold text-[#344B71]">1. Titolare del Trattamento</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Il Titolare del trattamento dei dati personali è ConsultantHUB, con sede in Italia. 
                Per qualsiasi informazione relativa al trattamento dei tuoi dati personali, puoi 
                contattarci attraverso la pagina Contatti del nostro sito.
              </p>
            </Card>

            <Card className="p-8 bg-white">
              <div className="flex items-center space-x-3 mb-4">
                <Eye className="w-6 h-6 text-[#344B71]" />
                <h2 className="text-xl font-semibold text-[#344B71]">2. Dati Raccolti</h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4">
                Raccogliamo le seguenti categorie di dati personali:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li><strong>Dati di registrazione:</strong> nome, email, password (criptata), tipo di account</li>
                <li><strong>Dati del profilo:</strong> categoria professionale, settore, competenze, esperienza, descrizione</li>
                <li><strong>Dati di contatto:</strong> telefono, email di contatto, sito web, link social</li>
                <li><strong>Dati degli annunci:</strong> titolo, descrizione, categoria, settore, priorità</li>
                <li><strong>Dati di navigazione:</strong> indirizzo IP, browser, sistema operativo, pagine visitate</li>
              </ul>
            </Card>

            <Card className="p-8 bg-white">
              <div className="flex items-center space-x-3 mb-4">
                <Lock className="w-6 h-6 text-[#344B71]" />
                <h2 className="text-xl font-semibold text-[#344B71]">3. Finalità del Trattamento</h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4">
                I tuoi dati personali sono trattati per le seguenti finalità:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Gestione dell'account e del profilo utente</li>
                <li>Erogazione dei servizi del marketplace (pubblicazione annunci, messaggistica)</li>
                <li>Comunicazioni relative al servizio</li>
                <li>Analisi statistiche aggregate per migliorare il servizio</li>
                <li>Adempimento di obblighi legali</li>
              </ul>
            </Card>

            <Card className="p-8 bg-white">
              <h2 className="text-xl font-semibold text-[#344B71] mb-4">4. Base Giuridica</h2>
              <p className="text-slate-600 leading-relaxed">
                Il trattamento dei tuoi dati è basato su: (a) il tuo consenso, (b) l'esecuzione di un 
                contratto di cui sei parte, (c) l'adempimento di obblighi legali, (d) il legittimo 
                interesse del Titolare.
              </p>
            </Card>

            <Card className="p-8 bg-white">
              <h2 className="text-xl font-semibold text-[#344B71] mb-4">5. Conservazione dei Dati</h2>
              <p className="text-slate-600 leading-relaxed">
                I tuoi dati personali sono conservati per il tempo necessario al conseguimento delle 
                finalità per cui sono stati raccolti e comunque non oltre 10 anni dalla cessazione del 
                rapporto contrattuale, salvo obblighi di legge che impongano una conservazione più lunga.
              </p>
            </Card>

            <Card className="p-8 bg-white">
              <h2 className="text-xl font-semibold text-[#344B71] mb-4">6. Diritti dell'Interessato</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Ai sensi del GDPR, hai diritto di:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li>Accedere ai tuoi dati personali</li>
                <li>Rettificare i dati inesatti</li>
                <li>Cancellare i tuoi dati ("diritto all'oblio")</li>
                <li>Limitare il trattamento</li>
                <li>Opporti al trattamento</li>
                <li>Portabilità dei dati</li>
                <li>Revocare il consenso in qualsiasi momento</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                Per esercitare questi diritti, visita la pagina{' '}
                <a href="/gestione-privacy" className="text-[#344B71] underline hover:no-underline">
                  Gestione Privacy
                </a>.
              </p>
            </Card>

            <Card className="p-8 bg-white">
              <h2 className="text-xl font-semibold text-[#344B71] mb-4">7. Cookie</h2>
              <p className="text-slate-600 leading-relaxed">
                Utilizziamo cookie tecnici necessari per il funzionamento del sito e cookie analitici 
                per comprendere come gli utenti interagiscono con il servizio. Per maggiori informazioni 
                e per gestire le tue preferenze, consulta la sezione Cookie nella pagina Gestione Privacy.
              </p>
            </Card>

            <Card className="p-8 bg-white">
              <div className="flex items-center space-x-3 mb-4">
                <Mail className="w-6 h-6 text-[#344B71]" />
                <h2 className="text-xl font-semibold text-[#344B71]">8. Contatti</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Per qualsiasi domanda relativa alla presente Privacy Policy o al trattamento dei tuoi 
                dati personali, puoi contattarci attraverso la{' '}
                <a href="/contatti" className="text-[#344B71] underline hover:no-underline">
                  pagina Contatti
                </a>.
              </p>
            </Card>
          </div>

          {/* Footer */}
          <div className="mt-12 text-center">
            <Button
              onClick={() => navigate('/gestione-privacy')}
              className="bg-[#344B71] hover:bg-[#344B71] text-white px-8 rounded-full shadow-lg hover:shadow-xl transition-all"
            >
              Gestisci le tue Preferenze Privacy
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPage;
